import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Switch, Redirect } from "react-router-dom";

import LoggedInLayout from "../layout";
import BackdropLoading from "../components/BackdropLoading";
import RouteFallback from "../components/RouteFallback";
import AppErrorBoundary from "../components/AppErrorBoundary";
import { AuthProvider, AuthContext } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import { EmailDraftsProvider } from "../context/EmailDrafts/EmailDraftsContext";
import { ToastProvider, ToastContainer } from "../components/Toast";
// ⚠️ Padrão do projeto: ToastProvider/ToastContainer (components/Toast)
import Route from "./Route";

// 🚀 Páginas críticas carregadas imediatamente
import Login from "../pages/Login/";
import Landing from "../pages/Landing/";
import OAuthCallback from "../pages/OAuthCallback/";

// 🔄 Lazy Loading - Páginas carregadas sob demanda
const Dashboard = lazy(() => import("../pages/Dashboard/"));
const TicketResponsiveContainer = lazy(() => import("../pages/TicketResponsiveContainer"));
const Connections = lazy(() => import("../pages/Connections/"));
const SettingsCustom = lazy(() => import("../pages/SettingsCustom/"));
const ExpiredSubscription = lazy(() => import("../pages/ExpiredSubscription/"));
const Users = lazy(() => import("../pages/Users"));
const Contacts = lazy(() => import("../pages/Contacts/"));
const ContactImportPage = lazy(() => import("../pages/Contacts/import"));
const MomentsChat = lazy(() => import("../pages/Moments"));
const Chat = lazy(() => import("../pages/Chat"));
const Queues = lazy(() => import("../pages/Queues/"));
const Tags = lazy(() => import("../pages/Tags/"));
const MessagesAPI = lazy(() => import("../pages/MessagesAPI/"));
const Helps = lazy(() => import("../pages/Helps/"));
const ContactLists = lazy(() => import("../pages/ContactLists/"));
const ContactListItems = lazy(() => import("../pages/ContactListItems/"));
const Companies = lazy(() => import("../pages/Companies/"));
const QuickMessages = lazy(() => import("../pages/QuickMessages/"));
const Schedules = lazy(() => import("../pages/Schedules"));
const Campaigns = lazy(() => import("../pages/Campaigns"));
const CampaignReport = lazy(() => import("../pages/CampaignReport"));
const CampaignBlacklist = lazy(() => import("../pages/CampaignBlacklist"));
const CampaignTemplates = lazy(() => import("../pages/CampaignTemplates"));
const Annoucements = lazy(() => import("../pages/Annoucements"));
// const Prompts = lazy(() => import("../pages/Prompts")); // Removido: Usar AIConfigurations
const AIConfigurations = lazy(() => import("../pages/AIConfigurations"));
const AITools = lazy(() => import("../pages/AITools")); // 🔧 AI Tools Framework
const AIPlayground = lazy(() => import("../pages/AIPlayground"));
const UnbrowseWebSessions = lazy(() => import("../components/UnbrowseWebSessions")); // 🔍 Unbrowse Web Sessions
const SmartAIDashboard = lazy(() => import("../pages/SmartAIDashboard"));
const AllConnections = lazy(() => import("../pages/AllConnections/"));
const Reports = lazy(() => import("../pages/Reports"));
const ReportsAdvanced = lazy(() => import("../pages/ReportsAdvanced"));
const TicketClassification = lazy(() => import("../pages/TicketClassification"));
const TicketTabsSettings = lazy(() => import("../pages/TicketTabsSettings"));
const QueueIntegration = lazy(() => import("../pages/QueueIntegration"));
const GoogleSpeechIntegration = lazy(() => import("../pages/GoogleSpeechIntegration"));
const Files = lazy(() => import("../pages/Files/"));
const ToDoList = lazy(() => import("../pages/ToDoList/"));
const Tasks = lazy(() => import("../pages/Tasks/"));
const Kanban = lazy(() => import("../pages/Kanban"));
const TagsKanban = lazy(() => import("../pages/TagsKanban"));
const Flows = lazy(() => import("../pages/Flows"));
const FlowsList = lazy(() => import("../pages/FlowsList"));
const WhatsappFlows = lazy(() => import("../pages/WhatsappFlows"));
const FlowBuilderPage = lazy(() => import("../pages/FlowBuilderPage"));
const FlowAnalytics = lazy(() => import("../pages/FlowAnalytics"));
const ContactWallets = lazy(() => import("../pages/ContactWallets"));
const ProfilePermissions = lazy(() => import("../pages/ProfilePermissions"));
const StorageManagement = lazy(() => import("../pages/StorageManagement"));
const BackupRestore = lazy(() => import("../pages/BackupRestore"));
const MobileAppQRCode = lazy(() => import("../pages/MobileAppQRCode"));
const IFoodOrders = lazy(() => import("../pages/IFoodOrders"));
const IFoodCatalog = lazy(() => import("../pages/IFoodCatalog"));
const IFoodReviews = lazy(() => import("../pages/IFoodReviews"));
const IFoodFinancial = lazy(() => import("../pages/IFoodFinancial"));
const IFoodPromotions = lazy(() => import("../pages/IFoodPromotions"));
const AdminFinanceiro = lazy(() => import("../pages/AdminFinanceiro"));
const Financeiro = lazy(() => import("../pages/Financeiro"));
const Signup = lazy(() => import("../pages/Signup"));
const AuditLogs = lazy(() => import("../pages/AuditLogs"));
const StripePayments = lazy(() => import("../pages/StripePayments"));
const Pricing = lazy(() => import("../pages/Pricing"));
const EmailConnections = lazy(() => import("../pages/EmailConnections"));
const EmailTemplates = lazy(() => import("../pages/EmailTemplates"));
const WhatsAppGroups = lazy(() => import("../pages/WhatsAppGroups"));
const WhatsAppCommunities = lazy(() => import("../pages/WhatsAppCommunities"));
const LeadDashboard = lazy(() => import("../pages/LeadDashboard"));
const Automations = lazy(() => import("../pages/Automations"));
const JourneyDashboard = lazy(() => import("../pages/JourneyDashboard"));
const Callbacks = lazy(() => import("../pages/Callbacks"));
const VoiceApiSettings = lazy(() => import("../pages/VoiceApiSettings"));
const ApiV1Settings = lazy(() => import("../pages/ApiV1Settings"));
const HelpCenter = lazy(() => import("../pages/HelpCenter"));

const Routes = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return null;
  }

  return (
    <AppErrorBoundary>
    <BrowserRouter>
      <ToastProvider>
        <AuthProvider>
          <TicketsContextProvider>
            <Switch>
              <Route exact path="/landing" component={Landing} />
              <Route exact path="/login" component={Login} />
              <Route exact path="/expired-subscription">
                <Suspense fallback={<RouteFallback />}>
                  <ExpiredSubscription />
                </Suspense>
              </Route>
              <Route exact path="/signup">
                <Suspense fallback={<RouteFallback />}>
                  <Signup />
                </Suspense>
              </Route>
              <Route exact path="/pricing">
                <Suspense fallback={<RouteFallback />}>
                  <Pricing />
                </Suspense>
              </Route>
              <Route exact path="/planos">
                <Suspense fallback={<RouteFallback />}>
                  <Pricing />
                </Suspense>
              </Route>
              {/* 🔗 OAuth Callback - Rota pública para Embedded Signup */}
              <Route exact path="/oauth-callback" component={OAuthCallback} />
              <AuthContext.Consumer>
                {({ isAuth, subscriptionExpired, loading: authLoading }) => {
                  const pathname = window.location.pathname;
                  const isPublicRoute =
                    pathname === "/login" ||
                    pathname === "/signup" ||
                    pathname === "/landing" ||
                    pathname === "/pricing" ||
                    pathname === "/planos" ||
                    pathname === "/expired-subscription" ||
                    pathname === "/oauth-callback";

                  if (authLoading) {
                    return <BackdropLoading />;
                  }

                  // Assinatura vencida: bloquear app logado e mandar para renovação
                  if (subscriptionExpired) {
                    return pathname === "/expired-subscription" ? null : (
                      <Redirect to="/expired-subscription" />
                    );
                  }

                  // Em rotas públicas, não forçar Redirect para evitar loops
                  if (!isAuth && isPublicRoute) {
                    return null;
                  }

                  return isAuth ? (
                    <WhatsAppsProvider>
                      <EmailDraftsProvider>
                      <LoggedInLayout>
                        <Suspense fallback={<RouteFallback />}>
                          <Route
                            exact
                            path="/financeiro"
                            component={Financeiro}
                            isPrivate
                          />

                          <Route
                            exact
                            path="/companies"
                            component={Companies}
                            isPrivate
                          />
                          <Route
                            exact
                            path="/admin-financeiro"
                            component={AdminFinanceiro}
                            isPrivate
                          />
                          <Route
                            exact
                            path="/stripe-payments"
                            component={StripePayments}
                            isPrivate
                          />
                          <Route
                            exact
                            path="/audit-logs"
                            component={AuditLogs}
                            isPrivate
                          />
                          {/* ✅ Rota padrão: Atendimento (Tickets) */}
                          <Route exact path="/" component={TicketResponsiveContainer} isPrivate />

                          {/* ✅ Dashboard continua disponível */}
                          <Route exact path="/dashboard" component={Dashboard} isPrivate />
                        
                        <Route
                          exact
                          path="/flows"
                          component={FlowsList}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/flows/:flowId"
                          component={Flows}
                          isPrivate
                        />
                        
                        <Route
                          exact
                          path="/tickets/:ticketId?"
                          component={TicketResponsiveContainer}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/connections"
                          component={Connections}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/ifood/orders"
                          component={IFoodOrders}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/ifood/catalog"
                          component={IFoodCatalog}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/ifood/reviews"
                          component={IFoodReviews}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/ifood/financial"
                          component={IFoodFinancial}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/ifood/promotions"
                          component={IFoodPromotions}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/quick-messages"
                          component={QuickMessages}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/todolist"
                          component={ToDoList}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/tasks"
                          component={Tasks}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/leads"
                          component={LeadDashboard}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/automations"
                          component={Automations}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/journey"
                          component={JourneyDashboard}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/callbacks"
                          component={Callbacks}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/schedules"
                          component={Schedules}
                          isPrivate
                        />
                        <Route exact path="/tags" component={Tags} isPrivate />
                        <Route
                          exact
                          path="/contacts"
                          component={Contacts}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/contacts/import"
                          component={ContactImportPage}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/helps"
                          component={Helps}
                          isPrivate
                        />
                        <Route
                          path="/help-center/:categoryId?/:articleId?"
                          component={HelpCenter}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/users"
                          component={Users}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/messages-api"
                          component={MessagesAPI}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/settings"
                          component={SettingsCustom}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/queues"
                          component={Queues}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/whatsapp-flows"
                          component={WhatsappFlows}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/whatsapp-flows/:id/edit"
                          component={FlowBuilderPage}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/whatsapp-flows/:id/analytics"
                          component={FlowAnalytics}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/contact-wallets"
                          component={ContactWallets}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/profile-permissions"
                          component={ProfilePermissions}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/storage-management"
                          component={StorageManagement}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/backup-restore"
                          component={BackupRestore}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/mobile-app-qr"
                          component={MobileAppQRCode}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/reports"
                          component={Reports}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/reports-advanced"
                          component={ReportsAdvanced}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/ticket-classification"
                          component={TicketClassification}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/ticket-tabs-settings"
                          component={TicketTabsSettings}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/queue-integration"
                          component={QueueIntegration}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/integrations/google-speech"
                          component={GoogleSpeechIntegration}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/integrations/voice-api"
                          component={VoiceApiSettings}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/integrations/public-api"
                          component={ApiV1Settings}
                          isPrivate
                          requiredPermission="automation.publicApi"
                        />
                        <Route
                          exact
                          path="/announcements"
                          component={Annoucements}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/chats/:id?"
                          component={Chat}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/files"
                          component={Files}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/moments"
                          component={MomentsChat}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/Kanban"
                          component={Kanban}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/TagsKanban"
                          component={TagsKanban}
                          isPrivate
                        />
                        {/* Redirect antigo /prompts para nova página */}
                        <Route
                          exact
                          path="/prompts"
                          render={() => <Redirect to="/ai-configurations" />}
                        />
                        <Route
                          exact
                          path="/ai-configurations"
                          component={AIConfigurations}
                          isPrivate
                          requiredPermission="automation.talkAI"
                        />
                        <Route
                          exact
                          path="/ai-tools"
                          component={AITools}
                          isPrivate
                          requiredPermission="automation.aiTools"
                        />
                        <Route
                          exact
                          path="/ai-tools/web-sessions"
                          component={UnbrowseWebSessions}
                          isPrivate
                          requiredPermission="automation.aiTools"
                        />
                        <Route
                          exact
                          path="/ai-playground"
                          component={AIPlayground}
                          isPrivate
                          requiredPermission="automation.talkAI"
                        />
                        <Route
                          exact
                          path="/smart-ai-dashboard"
                          component={SmartAIDashboard}
                          isPrivate
                          requiredPermission="automation.talkAI"
                        />
                        <Route
                          exact
                          path="/email-connections"
                          component={EmailConnections}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/email-templates"
                          component={EmailTemplates}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/whatsapp-groups"
                          component={WhatsAppGroups}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/whatsapp-communities"
                          component={WhatsAppCommunities}
                          isPrivate
                        />
                        <Route
                          exact
                          path="/allConnections"
                          component={AllConnections}
                          isPrivate
                        />
                        {showCampaigns && (
                          <>
                            <Route
                              exact
                              path="/contact-lists"
                              component={ContactLists}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/contact-lists/:contactListId/contacts"
                              component={ContactListItems}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/campaigns"
                              component={Campaigns}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/campaign/:campaignId/report"
                              component={CampaignReport}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/campaign-blacklist"
                              component={CampaignBlacklist}
                              isPrivate
                            />
                            <Route
                              exact
                              path="/campaign-templates"
                              component={CampaignTemplates}
                              isPrivate
                            />
                          </>
                        )}
                        </Suspense>
                      </LoggedInLayout>
                      </EmailDraftsProvider>
                    </WhatsAppsProvider>
                  ) : (
                    <Redirect to="/login" />
                  );
                }}
              </AuthContext.Consumer>
            </Switch>
            <ToastContainer />
          </TicketsContextProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
    </AppErrorBoundary>
  );
};

export default Routes;
